import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { authorizedFetch } from '../utils/api';

// Spinner copied from KnowledgeBase
function Spinner() {
  return (
    <div className="flex justify-center items-center h-32">
      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
      </svg>
    </div>
  );
}

export default function Upload({ buyerId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const editingLook = location.state?.look;
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [designer, setDesigner] = useState('');
  const [season, setSeason] = useState('');
  const [collection, setCollection] = useState('');
  const [tags, setTags] = useState('');
  const [machineScore, setMachineScore] = useState(null);
  const [manualScore, setManualScore] = useState('');
  const [lookId, setLookId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const fileInputRef = useRef();
  const [renderTime, setRenderTime] = useState(null);
  const [showRenderTime, setShowRenderTime] = useState(false);
  const renderStartRef = React.useRef(null);

  // Prefill logic
  useEffect(() => {
    if (editingLook) {
      setDesigner(editingLook.designer || '');
      setSeason(editingLook.season || '');
      setCollection(editingLook.collection || '');
      setTags(Array.isArray(editingLook.tags) ? editingLook.tags.join(', ') : (editingLook.tags || ''));
      setMachineScore(editingLook.score ?? null);
      setManualScore(editingLook.buyer_score ?? '');
      setLookId(editingLook.look_id || '');
      setImageUrl(editingLook.image_url || '');
      setPreviewUrl(editingLook.image_url || '');
      setFile(null);
    }
    renderStartRef.current = performance.now();
    setTimeout(() => {
      const end = performance.now();
      const seconds = ((end - renderStartRef.current) / 1000).toFixed(2);
      setRenderTime(seconds);
      setShowRenderTime(true);
      setTimeout(() => setShowRenderTime(false), 5000);
    }, 0);
  }, [editingLook]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setMachineScore(null);
    setManualScore('');
    setLookId('');
    setImageUrl('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setPreviewUrl(URL.createObjectURL(e.dataTransfer.files[0]));
      setMachineScore(null);
      setManualScore('');
      setLookId('');
      setImageUrl('');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file && !editingLook) {
      setToast('Please select a file.');
      return;
    }
    setUploading(true);
    setToast('');
    try {
      const res = await authorizedFetch('/upload-look', {
        method: 'POST',
        body: new FormData(),
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setMachineScore(data.machine_score);
      setLookId(data.look_id);
      setImageUrl(data.image_url);
      setToast('Upload complete!');
    } catch (err) {
      setToast('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveScore = async () => {
    if (!lookId && !editingLook) {
      setToast('Please enter a manual score.');
      return;
    }
    setSaving(true);
    setToast('');
    try {
      const res = await authorizedFetch(`/look/${editingLook ? editingLook.look_id : lookId}`, {
        method: 'PATCH',
        body: new FormData(),
      });
      if (!res.ok) throw new Error('Update failed');
      setToast('Score updated!');
    } catch (err) {
      setToast('Update failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (uploading) return <Spinner />;

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow animate-fade-in" style={{ position: 'relative' }}>
      <h1 className="text-2xl font-bold mb-6">{editingLook ? 'Edit Fashion Look' : 'Upload Fashion Look'}</h1>
      <form onSubmit={handleUpload}>
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4 flex flex-col items-center justify-center cursor-pointer bg-gray-50 hover:bg-gray-100"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current.click()}
        >
          {previewUrl ? (
            editingLook && !file ? (
              editingLook.image_url && editingLook.image_url.match(/\.(mp4)$/i) ? (
                <video src={editingLook.image_url} controls className="max-h-64 mb-2" />
              ) : (
                <img src={editingLook.image_url} alt="preview" className="max-h-64 mb-2" />
              )
            ) : file && file.type.startsWith('image') ? (
              <img src={previewUrl} alt="preview" className="max-h-64 mb-2" />
            ) : file && file.type.startsWith('video') ? (
              <video src={previewUrl} controls className="max-h-64 mb-2" />
            ) : null
          ) : (
            <span className="text-gray-400">Drag & drop or click to select an image/video</span>
          )}
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.mp4"
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileChange}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <input
            type="text"
            className="border rounded px-3 py-2"
            placeholder="Designer"
            value={designer}
            onChange={e => setDesigner(e.target.value)}
          />
          <input
            type="text"
            className="border rounded px-3 py-2"
            placeholder="Season"
            value={season}
            onChange={e => setSeason(e.target.value)}
          />
          <input
            type="text"
            className="border rounded px-3 py-2"
            placeholder="Collection"
            value={collection}
            onChange={e => setCollection(e.target.value)}
          />
          <input
            type="text"
            className="border rounded px-3 py-2"
            placeholder="Tags (comma separated)"
            value={tags}
            onChange={e => setTags(e.target.value)}
          />
        </div>
        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 rounded font-semibold hover:bg-blue-700 transition"
          disabled={uploading}
        >
          {uploading ? (editingLook ? 'Updating...' : 'Uploading...') : (editingLook ? 'Update Look' : 'Upload & Get Machine Score')}
        </button>
      </form>
      {(machineScore !== null || editingLook) && (
        <div className="mt-6 p-4 bg-gray-100 rounded shadow">
          <div className="mb-2">Machine Score: <span className="font-bold">{machineScore ?? editingLook?.machine_score ?? editingLook?.score}</span></div>
          <div className="mb-2">
            <label className="block mb-1">Buyer Score:</label>
            <input
              type="number"
              min="0"
              max="100"
              className="border rounded px-3 py-2 w-32"
              value={manualScore}
              onChange={e => setManualScore(e.target.value)}
              disabled={saving}
            />
            <button
              className="ml-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
              onClick={handleSaveScore}
              disabled={saving}
              type="button"
            >
              {saving ? (editingLook ? 'Updating...' : 'Saving...') : (editingLook ? 'Update Score' : 'Confirm + Save')}
            </button>
          </div>
          {imageUrl && (
            <div className="mt-2">
              <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">View Uploaded Media</a>
            </div>
          )}
        </div>
      )}
      {toast && (
        <div className="mt-4 p-2 bg-yellow-100 border border-yellow-300 text-yellow-800 rounded">{toast}</div>
      )}
      {showRenderTime && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50 }} className="bg-gray-800 text-white text-xs px-3 py-1 rounded shadow animate-fade-in">
          Render Time: {renderTime} seconds
        </div>
      )}
    </div>
  );
} 